/**
 * PATH: packages/drafting/src/ir/validate.ts
 * FILE_ID: DRAFTING-IR-VALIDATE-001
 * PURPOSE: The code-sequence validator (pure, composable). Each
 *   validator is f(ir) -> ValidationError[]; runValidators composes them.
 * WHY: Nothing renders/exports/sends unless the IR passes (or the user
 *   accepts a flagged draft). This is the deterministic gate the agentic
 *   loop self-corrects against (DRAFTING_STUDIO_SCOPE.md §8.3). P1 ships
 *   the geometry-correctness subset; the scale/dimension validators are
 *   wired here too so the contract is stable for P3.
 * NOTE: Hand-rolled type guards (no zod) — see schema.ts header.
 */

import {
  type DrawingIR,
  type Entity,
  type Point,
  type ValidationError,
  type ValidationReport,
} from './schema'
import { distance, parseScaleRatio, realWorldLength } from './normalize'

type Validator = (ir: DrawingIR) => ValidationError[]

function isFinitePoint(p: unknown): p is Point {
  return (
    Array.isArray(p) &&
    p.length === 2 &&
    Number.isFinite(p[0]) &&
    Number.isFinite(p[1])
  )
}

/** Every point referenced by an entity, for finite/bounds checks. */
function entityPoints(e: Entity): Point[] {
  switch (e.type) {
    case 'line':
      return [e.a, e.b]
    case 'polyline':
    case 'hatch':
      return e.points
    case 'rect':
      return [
        [e.x, e.y],
        [e.x + e.w, e.y + e.h],
      ]
    case 'circle':
    case 'arc':
      return [[e.cx, e.cy]]
    case 'text':
      return [e.at]
    case 'dimension':
      return [e.a, e.b]
    case 'leader':
      return [e.from, e.to]
    case 'group':
      return []
  }
}

/** schemaValid — top-level shape + required fields present. */
const schemaValid: Validator = (ir) => {
  const errors: ValidationError[] = []
  if (!ir || typeof ir !== 'object') {
    return [{ code: 'schema.root', msg: 'IR is not an object', severity: 'error' }]
  }
  if (!ir.ir_version) {
    errors.push({ code: 'schema.version', msg: 'Missing ir_version', severity: 'error' })
  }
  if (!ir.meta || !ir.meta.units || !ir.meta.scale?.ratio) {
    errors.push({ code: 'schema.meta', msg: 'meta.units and meta.scale.ratio are required', severity: 'error' })
  }
  if (!Array.isArray(ir.layers)) {
    errors.push({ code: 'schema.layers', msg: 'layers must be an array', severity: 'error' })
  }
  if (!Array.isArray(ir.entities)) {
    errors.push({ code: 'schema.entities', msg: 'entities must be an array', severity: 'error' })
  }
  return errors
}

/** idsUnique — entity AND layer ids must each be unique. */
const idsUnique: Validator = (ir) => {
  const errors: ValidationError[] = []
  const seenEntity = new Set<string>()
  for (const e of ir.entities ?? []) {
    if (!e.id) {
      errors.push({ code: 'id.missing', msg: 'Entity has no id', severity: 'error' })
      continue
    }
    if (seenEntity.has(e.id)) {
      errors.push({ code: 'id.dupe', msg: `Duplicate entity id "${e.id}"`, entityId: e.id, severity: 'error' })
    }
    seenEntity.add(e.id)
  }
  const seenLayer = new Set<string>()
  for (const l of ir.layers ?? []) {
    if (seenLayer.has(l.id)) {
      errors.push({ code: 'layer.dupe', msg: `Duplicate layer id "${l.id}"`, severity: 'error' })
    }
    seenLayer.add(l.id)
  }
  return errors
}

/** layerRefs — every entity references a layer that exists. */
const layerRefs: Validator = (ir) => {
  const layerIds = new Set((ir.layers ?? []).map((l) => l.id))
  const errors: ValidationError[] = []
  for (const e of ir.entities ?? []) {
    if (!layerIds.has(e.layer)) {
      errors.push({
        code: 'layer.ref',
        msg: `Entity "${e.id}" references unknown layer "${e.layer}"`,
        entityId: e.id,
        severity: 'error',
      })
    }
  }
  return errors
}

/** finiteGeometry — no NaN/Inf coordinates. */
const finiteGeometry: Validator = (ir) => {
  const errors: ValidationError[] = []
  for (const e of ir.entities ?? []) {
    for (const p of entityPoints(e)) {
      if (!isFinitePoint(p)) {
        errors.push({
          code: 'geom.finite',
          msg: `Entity "${e.id}" has a non-finite coordinate`,
          entityId: e.id,
          severity: 'error',
        })
        break
      }
    }
    if (e.type === 'circle' || e.type === 'arc') {
      if (!Number.isFinite(e.r) || e.r <= 0) {
        errors.push({ code: 'geom.radius', msg: `Entity "${e.id}" has a non-positive radius`, entityId: e.id, severity: 'error' })
      }
    }
  }
  return errors
}

/** scaleConsistency — meta.scale.ratio must parse. */
const scaleConsistency: Validator = (ir) => {
  if (!ir.meta?.scale?.ratio) return []
  if (!parseScaleRatio(ir.meta.scale.ratio)) {
    return [{ code: 'scale.ratio', msg: `Scale "${ir.meta.scale.ratio}" is not a valid ratio (expected e.g. "1:5")`, severity: 'error' }]
  }
  return []
}

/**
 * dimensionsResolve — a dimension's declared value should equal the
 * geometric distance between its endpoints converted by the drawing
 * scale (within tolerance). A mismatch is the classic "AI wrote 40mm but
 * the line is 55mm" hallucination — warn so the user sees it.
 */
const DIMENSION_TOLERANCE = 0.02 // 2%
const dimensionsResolve: Validator = (ir) => {
  const errors: ValidationError[] = []
  const ratio = ir.meta?.scale?.ratio
  for (const e of ir.entities ?? []) {
    if (e.type !== 'dimension' || e.value == null) continue
    const drawn = distance(e.a, e.b)
    const expected = realWorldLength(drawn, ratio)
    if (expected == null) continue
    const denom = Math.max(Math.abs(expected), 1e-6)
    if (Math.abs(expected - e.value) / denom > DIMENSION_TOLERANCE) {
      errors.push({
        code: 'dimension.mismatch',
        msg: `Dimension "${e.id}" says ${e.value} but geometry × scale ≈ ${expected.toFixed(1)}`,
        entityId: e.id,
        severity: 'warn',
      })
    }
  }
  return errors
}

const VALIDATORS: Validator[] = [
  schemaValid,
  idsUnique,
  layerRefs,
  finiteGeometry,
  scaleConsistency,
  dimensionsResolve,
]

/**
 * runValidators — compose all validators into one report.
 * `ok` is true when there are zero `error`-severity findings (warnings
 * do not block; the UI surfaces them but lets the user proceed).
 */
export function runValidators(ir: DrawingIR): ValidationReport {
  const errors = VALIDATORS.flatMap((v) => {
    try {
      return v(ir)
    } catch (err) {
      return [{ code: 'validator.threw', msg: `Validator crashed: ${String(err)}`, severity: 'error' as const }]
    }
  })
  return { ok: !errors.some((e) => e.severity === 'error'), errors }
}
